import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { KeyRound } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login, user, authReady } = useAuth();
  const navigate = useNavigate();

  // Əgər istifadəçi artıq daxil olubsa, dashboard-a yönləndir
  useEffect(() => {
    if (authReady && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authReady, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.userData) {
        toast.success('Xoş gəldiniz!');
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Login error:', error);
      
      let errorMessage = 'Giriş uğursuz oldu';
      
      if (error.code === 'auth/network-request-failed') {
        errorMessage = 'İnternet bağlantısı problemi. Yenidən cəhd edin.';
      } else if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        errorMessage = 'Email və ya şifrə yanlışdır';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email düzgün formatda deyil';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      toast.error('Email daxil edin');
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      toast.success('Şifrə sıfırlama linki email-inizə göndərildi');
      setShowResetModal(false);
      setResetEmail('');
    } catch (error) {
      console.error('Password reset error:', error);
      let errorMessage = 'Şifrə sıfırlama uğursuz oldu';
      
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'Bu email ilə istifadəçi tapılmadı';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Email düzgün formatda deyil';
      }
      
      toast.error(errorMessage);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4" data-testid="login-page">
      {/* Grid Pattern Background */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border-2 border-gray-200 p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-black mb-2">I Veb</h1>
            <p className="text-gray-600">Mağazanıza daxil olun</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password">Şifrə</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
              />
            </div>

            <div className="text-right">
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-sm text-gray-600 hover:text-black font-medium"
              >
                Şifrəmi unutdum
              </button>
            </div>

            <Button
              type="submit"
              className="w-full bg-black hover:bg-gray-800 text-white"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Giriş edilir...' : 'Daxil ol'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Hesabınız yoxdur?{' '}
              <Link to="/register" className="text-black font-semibold hover:underline" data-testid="register-link">
                Qeydiyyatdan keçin
              </Link>
            </p>
          </div>
        </div>

        <div className="mt-6">
          <p className="text-center">
            <Link to="/" className="text-sm text-gray-600 hover:text-black transition-colors" data-testid="home-link">
              ← Ana səhifəyə qayıt
            </Link>
          </p>
        </div>
      </div>

      {/* Password Reset Modal */}
      {showResetModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-black">Şifrəni Sıfırla</h2>
                <p className="text-sm text-gray-600">Email-inizi daxil edin</p>
              </div>
            </div>

            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <Label htmlFor="reset-email">Email</Label>
                <Input
                  id="reset-email"
                  type="email"
                  placeholder="email@example.com"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Şifrə sıfırlama linki bu email-ə göndəriləcək
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowResetModal(false);
                    setResetEmail('');
                  }}
                  className="flex-1"
                  disabled={resetLoading}
                >
                  Ləğv et
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-black hover:bg-gray-800 text-white"
                  disabled={resetLoading}
                >
                  {resetLoading ? 'Göndərilir...' : 'Link Göndər'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}